// src/pages/container_rental/components/RentalScheduleTable.tsx
//
// Read-only table of rental schedule rows with a per-row action menu.
// Uses a plain MUI Table (not DataGrid) because the row count is bounded
// (typically <= 36 rounds) and we want strong custom styling for round 1.

import { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { designTokens } from "../../../theme.ts";
import { RentalScheduleDto } from "../../../contracts/container_rental/types.ts";
import RoundStatusChip from "./RoundStatusChip.tsx";
import dayjs from "dayjs";

interface RentalScheduleTableProps {
  schedules: RentalScheduleDto[];
  onIssue: (round: RentalScheduleDto) => void;
  onSkip: (round: RentalScheduleDto) => void;
  onCancel: (round: RentalScheduleDto) => void;
}

const fmtDate = (iso: string) => {
  if (!iso) return "";
  const d = dayjs(iso);
  return d.isValid() ? d.format("YYYY.MM.DD") : iso;
};

const fmtKrw = (n: number) =>
  Number.isFinite(n) ? new Intl.NumberFormat("ko-KR").format(n) : "";

// 금액 셀 — 제네릭 monospace(브라우저 기본) 대신 디자인 토큰 mono 를 쓰고
// 자릿수가 세로로 맞도록 tabular-nums 를 함께 건다.
const moneySx = {
  fontFamily: designTokens.fonts.mono,
  fontVariantNumeric: "tabular-nums",
} as const;

interface RowActionsProps {
  row: RentalScheduleDto;
  onIssue: (r: RentalScheduleDto) => void;
  onSkip: (r: RentalScheduleDto) => void;
  onCancel: (r: RentalScheduleDto) => void;
}

function RowActions({ row, onIssue, onSkip, onCancel }: RowActionsProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (row.status !== "scheduled") {
    return null;
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={`${row.round}회차 액션`}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onIssue(row);
          }}
        >
          발행 완료 처리
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onSkip(row);
          }}
        >
          건너뜀 처리
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onCancel(row);
          }}
        >
          취소
        </MenuItem>
      </Menu>
    </>
  );
}

export default function RentalScheduleTable({
  schedules,
  onIssue,
  onSkip,
  onCancel,
}: RentalScheduleTableProps) {
  if (!schedules.length) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 4, borderRadius: "12px", textAlign: "center", color: "text.secondary" }}
      >
        <Typography variant="body2">발행 일정이 없습니다.</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ border: "1px solid", borderColor: "divider" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.100" }}>
            <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>회차</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, width: 130 }}>발행일</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, minWidth: 200 }}>사용기간</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>공급가액</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>부가세</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>합계</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, width: 110 }}>상태</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>비고</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, width: 56 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {schedules.map((s) => {
            const isFirst = s.round === 1;
            const reason =
              s.status === "skipped" || s.status === "cancelled"
                ? s.issuedMemo || "사유 없음"
                : undefined;

            const noteContent = (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {s.note}
                </Typography>
                {s.status === "issued" && (
                  <Typography variant="caption" sx={{ color: "success.main", fontWeight: 600 }}>
                    {s.invoiceNumber ? `세금계산서 ${s.invoiceNumber}` : "발행 완료"}
                    {s.issuedAt ? ` · ${fmtDate(s.issuedAt)}` : ""}
                  </Typography>
                )}
              </Box>
            );

            return (
              <TableRow
                key={s.id || s.round}
                sx={{
                  bgcolor: isFirst ? "rgba(255, 193, 7, 0.08)" : "transparent",
                  "&:hover": { bgcolor: "action.hover" },
                  opacity: s.status === "cancelled" || s.status === "skipped" ? 0.65 : 1,
                }}
              >
                <TableCell align="center">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      bgcolor: isFirst ? "warning.light" : "grey.200",
                      color: isFirst ? "warning.contrastText" : "text.primary",
                    }}
                  >
                    {s.round}회차
                  </Box>
                </TableCell>
                <TableCell align="center">{fmtDate(s.issueDate)}</TableCell>
                <TableCell align="center" sx={{ fontSize: "0.8rem" }}>
                  {fmtDate(s.periodStart)} ~ {fmtDate(s.periodEnd)}
                </TableCell>
                <TableCell align="right" sx={moneySx}>
                  {fmtKrw(s.supply)}
                </TableCell>
                <TableCell align="right" sx={{ ...moneySx, color: "text.secondary", fontSize: "0.8rem" }}>
                  {fmtKrw(s.vat)}
                </TableCell>
                <TableCell align="right" sx={{ ...moneySx, fontWeight: 700 }}>
                  {fmtKrw(s.total)}
                </TableCell>
                <TableCell align="center">
                  {reason ? (
                    <Tooltip title={`사유: ${reason}`} arrow>
                      <span>
                        <RoundStatusChip status={s.status} />
                      </span>
                    </Tooltip>
                  ) : (
                    <RoundStatusChip status={s.status} />
                  )}
                </TableCell>
                <TableCell>{noteContent}</TableCell>
                <TableCell align="center">
                  <RowActions
                    row={s}
                    onIssue={onIssue}
                    onSkip={onSkip}
                    onCancel={onCancel}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
